import { useState, useEffect, useCallback } from "react";
import supabase from "../supabase";
import { T, G, withAlpha } from "../theme";
import { getHealthAuthStatus, retryHealthAuthorization } from "../services/healthSync";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell
} from "recharts";

// ─── SHARED STYLE TOKENS ─────────────────────────────────────────────────────
// Used by PreventiveCareChecklist (light) — kept exactly as before
const S = {
  card: {
    background: G.cardSurface,
    border: G.hairline,
    boxShadow: G.shadow,
    backdropFilter: G.blurCard,
    WebkitBackdropFilter: G.blurCard,
    borderRadius: 12,
    padding: "20px 22px",
    fontFamily: "'DM Sans', sans-serif",
  },
  inner: {
    background: G.tileBg,
    border: `1px solid ${G.divider}`,
    borderRadius: 10,
    padding: "14px 16px",
  },
  title: { fontSize: 15, fontWeight: 700, color: T.text, marginBottom: 4 },
  sub:   { fontSize: 12, color: T.textMuted },
  btn: (outline) => ({
    background: outline ? "transparent" : T.accentFill,
    border: `1px solid ${T.accent}`,
    borderRadius: 8,
    padding: "9px 18px",
    fontSize: 13,
    fontWeight: 700,
    color: outline ? T.accent : "#fff",
    cursor: "pointer",
    fontFamily: "'DM Sans', sans-serif",
    transition: "opacity 0.15s",
  }),
  ghost: {
    background: "none",
    border: "none",
    fontSize: 12,
    color: T.textMuted,
    cursor: "pointer",
    fontFamily: "'DM Sans', sans-serif",
    padding: "4px 8px",
  },
};

// ─── LIGHT TOKENS for AppleHealthSection (matches S / THEMES.light) ──────────
const D = {
  card: {
    background: G.cardSurface,
    border: G.hairline,
    boxShadow: G.shadow,
    backdropFilter: G.blurCard,
    WebkitBackdropFilter: G.blurCard,
    borderRadius: 12,
    padding: "20px 22px",
    fontFamily: "'DM Sans', sans-serif",
  },
  inner: {
    background: G.tileBg,
    border: `1px solid ${G.divider}`,
    borderRadius: 10,
    padding: "14px 16px",
  },
  title:   { fontSize: 15, fontWeight: 700, color: T.text, marginBottom: 4 },
  sub:     { fontSize: 12, color: T.textMuted },
  accent:  T.accent,
  success: T.success,
  text:    T.text,
  muted:   T.textMuted,
};

// ─── HEALTH AUTHORIZATION FALLBACK (SCORIFY-APP-P) ────────────────────────────
// healthSync.authorizeHealth() retries a failed permission sheet once; if it
// still fails, status flips to 'failed' and this notice appears wherever
// Health is connected (onboarding sheet, Health tab card). It never blocks:
// it is informational, and the user can carry on without Health.
export function useHealthAuthStatus() {
  const [status, setStatus] = useState(getHealthAuthStatus);
  useEffect(() => {
    const on = (e) => setStatus(e?.detail || getHealthAuthStatus());
    window.addEventListener("scorify-health-auth", on);
    setStatus(getHealthAuthStatus()); // a change may have landed before mount
    return () => window.removeEventListener("scorify-health-auth", on);
  }, []);
  return status;
}

export function HealthAuthNotice({ userId, style }) {
  const status = useHealthAuthStatus();
  const [retrying, setRetrying] = useState(false);
  if (status !== "failed") return null;
  const tryAgain = async () => {
    if (retrying) return;
    setRetrying(true);
    try { await retryHealthAuthorization(userId); } finally { setRetrying(false); }
  };
  return (
    <div role="status" style={{ background: G.tileBg, border: `1px solid ${G.divider}`, borderRadius: 10, padding: "14px 16px", fontFamily: "'DM Sans', sans-serif", ...style }}>
      <div style={{ fontSize: 13, color: T.text, lineHeight: 1.6, marginBottom: 10 }}>
        Couldn't open Apple Health permissions. Tap to try again, or turn it on in Settings &rarr; Health &rarr; Data Access &amp; Devices &rarr; Scorify.
      </div>
      <button
        onClick={tryAgain}
        disabled={retrying}
        style={{ background: "transparent", border: `1px solid ${withAlpha(T.accentFill, 0.35)}`, color: T.accent, borderRadius: 8, padding: "8px 14px", fontSize: 13, fontWeight: 700, cursor: retrying ? "default" : "pointer", opacity: retrying ? 0.6 : 1, fontFamily: "inherit" }}
      >
        {retrying ? "Trying…" : "Try again"}
      </button>
    </div>
  );
}

// ─── PREVENTIVE CARE ─────────────────────────────────────────────────────────

const QUICK_ADD = ["Annual Doctor Visit", "Dental Cleaning", "Eye Exam"];

const INP = {
  width: "100%", boxSizing: "border-box",
  background: G.cardSurface, border: `1px solid ${T.border}`,
  borderRadius: 8, padding: "10px 13px",
  fontSize: 16, // 16px minimum — prevents iOS zoom on focus
  color: T.text, fontFamily: "'DM Sans', sans-serif",
  outline: "none",
};

function fmtDate(str) {
  if (!str) return null;
  const d = new Date(str + "T00:00:00");
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function fmtTime(str) {
  if (!str) return null;
  const [h, m] = str.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  const hr = h % 12 || 12;
  return `${hr}:${String(m).padStart(2, "0")} ${ampm}`;
}

export function PreventiveCareChecklist({ user, isDemo, demoItems }) {
  const [items,   setItems]   = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving,  setSaving]  = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: "", due_date: "", time: "" });

  // ── Fetch — Supabase is sole source of truth ──────────────────────────────
  const fetchItems = useCallback(async () => {
    if (isDemo) { setItems(demoItems || []); return; }
    if (!user?.id) return;
    setLoading(true);
    const { data } = await supabase
      .from("preventive_care")
      .select("id, title, due_date, time, status")
      .eq("user_id", user.id)
      .order("due_date", { ascending: true, nullsFirst: false });
    setItems(data || []);
    setLoading(false);
  }, [user?.id, isDemo, demoItems]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  // ── Add ───────────────────────────────────────────────────────────────────
  const handleAdd = async () => {
    if (!form.title.trim() || !user?.id) return;
    setSaving(true);
    await supabase.from("preventive_care").insert([{
      user_id:  user.id,
      title:    form.title.trim(),
      due_date: form.due_date  || null,
      time:     form.time      || null,
      status:   "upcoming",
    }]);
    setForm({ title: "", due_date: "", time: "" });
    setShowForm(false);
    setSaving(false);
    await fetchItems(); // always re-fetch — Supabase is truth
  };

  // ── Remove ────────────────────────────────────────────────────────────────
  const handleRemove = async (id) => {
    await supabase.from("preventive_care").delete().eq("id", id);
    await fetchItems(); // re-fetch so list matches DB exactly
  };

  // ── Helpers ───────────────────────────────────────────────────────────────
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const isOverdue = (d) => d && new Date(d + "T00:00:00") < today;

  return (
    <div style={S.card}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <div style={S.title}>Preventive Care</div>
        <button style={S.btn(true)} onClick={() => { setShowForm(v => !v); setForm({ title: "", due_date: "", time: "" }); }}>
          {showForm ? "Cancel" : "+ Add"}
        </button>
      </div>

      {/* ── Add form ── */}
      {showForm && (
        <div style={{ ...S.inner, marginBottom: 14 }}>
          {/* Quick-add chips */}
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
            {QUICK_ADD.map(label => (
              <button
                key={label}
                onClick={() => setForm(p => ({ ...p, title: label }))}
                style={{
                  background: form.title === label ? withAlpha(T.accentFill,0.1) : T.surfaceAlt,
                  border: `1px solid ${form.title === label ? T.accent : T.border}`,
                  borderRadius: 20, padding: "5px 12px",
                  fontSize: 12, color: form.title === label ? T.accent : T.textMuted,
                  cursor: "pointer", fontFamily: "'DM Sans', sans-serif", fontWeight: 600,
                }}
              >{label}</button>
            ))}
          </div>

          {/* Title */}
          <div style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 12, color: T.accent, fontWeight: 700, marginBottom: 5 }}>Appointment</div>
            <input
              value={form.title}
              onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
              onKeyDown={e => e.key === "Enter" && handleAdd()}
              placeholder="e.g. Annual Physical, Blood Work…"
              style={INP}
            />
          </div>

          {/* Date + Time row */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 }}>
            <div>
              <div style={{ fontSize: 12, color: T.accent, fontWeight: 700, marginBottom: 5 }}>Date <span style={{ color: "#666", fontWeight: 400 }}>(optional)</span></div>
              {/* Clear control: an iOS date picker offers no way back to empty
                  once set, and this field is optional. */}
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <input
                  type="date"
                  value={form.due_date}
                  onChange={e => setForm(p => ({ ...p, due_date: e.target.value }))}
                  style={{ ...INP, flex: 1, minWidth: 0, boxSizing: "border-box", WebkitAppearance: "none", colorScheme: "light" }}
                />
                {form.due_date ? (
                  <button type="button" onClick={() => setForm(p => ({ ...p, due_date: "" }))}
                    style={{ flexShrink: 0, background: "transparent", border: `1px solid ${T.border}`, color: T.textMuted, borderRadius: 8, padding: "8px 10px", fontSize: 12, cursor: "pointer" }}>
                    Clear
                  </button>
                ) : null}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 12, color: T.accent, fontWeight: 700, marginBottom: 5 }}>Time <span style={{ color: "#666", fontWeight: 400 }}>(optional)</span></div>
              <input
                type="time"
                value={form.time}
                onChange={e => setForm(p => ({ ...p, time: e.target.value }))}
                style={{ ...INP, colorScheme: "light" }}
              />
            </div>
          </div>

          <button
            onClick={handleAdd}
            disabled={saving || !form.title.trim()}
            style={{ ...S.btn(false), width: "100%", fontSize: 15, opacity: saving || !form.title.trim() ? 0.5 : 1 }}
          >
            {saving ? "Saving…" : "Add Appointment"}
          </button>
        </div>
      )}

      {/* ── List ── */}
      {loading ? (
        <div style={{ textAlign: "center", padding: "20px 0", fontSize: 13, color: "#666" }}>Loading…</div>
      ) : items.length === 0 ? (
        <div style={{ textAlign: "center", padding: "20px 0" }}>
          <div style={{ fontSize: 13, color: "#666", marginBottom: 10 }}>No appointments scheduled.</div>
          <button style={{ ...S.btn(true), fontSize: 13, padding: "7px 18px" }} onClick={() => setShowForm(true)}>Schedule one</button>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {items.map(item => {
            const overdue     = isOverdue(item.due_date);
            const borderColor = overdue ? withAlpha(T.danger,0.35) : withAlpha(T.accent,0.25);
            const bgColor     = overdue ? withAlpha(T.danger,0.06) : withAlpha(T.accent,0.06);
            const dateColor   = overdue ? T.danger : T.accent;
            const badge       = overdue ? "Overdue" : "Upcoming";
            const dateLine    = [fmtDate(item.due_date), fmtTime(item.time)].filter(Boolean).join(" · ");
            return (
              <div key={item.id} style={{ background: bgColor, border: `1px solid ${borderColor}`, borderRadius: 10, padding: "11px 14px", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: T.text, marginBottom: dateLine ? 3 : 0 }}>{item.title}</div>
                  {dateLine && (
                    <div style={{ fontSize: 12, color: dateColor, fontWeight: 600 }}>{badge} · {dateLine}</div>
                  )}
                </div>
                <button
                  onClick={() => { if (window.confirm(`Remove "${item.title}"?`)) handleRemove(item.id); }}
                  aria-label={`Remove ${item.title}`}
                  title="Remove"
                  style={{ background: "none", border: "none", color: T.textMuted, cursor: "pointer", fontSize: 18, padding: "0 2px", flexShrink: 0, lineHeight: 1 }}
                >×</button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── APPLE HEALTH SECTION ─────────────────────────────────────────────────────
// Reads from the `workouts` and `daily_activity` tables (populated by healthSync.js).
// Shows weekly workout stats, steps today, 4-week workout chart, 7-day steps chart.

function localDay(daysAgo) {
  const d = new Date(Date.now() - daysAgo * 86400000);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const DEMO_WORKOUTS = [
  { type: "Run",  date: localDay(1),  distance_m: 4828, duration_min: 28 },
  { type: "Run",  date: localDay(3),  distance_m: 6437, duration_min: 38 },
  { type: "Walk", date: localDay(5),  distance_m: 3218, duration_min: 45 },
  { type: "Run",  date: localDay(8),  distance_m: 5150, duration_min: 30 },
  { type: "Run",  date: localDay(10), distance_m: 7200, duration_min: 42 },
  { type: "WeightTraining", date: localDay(12), distance_m: null, duration_min: 55 },
  { type: "Run",  date: localDay(15), distance_m: 4500, duration_min: 26 },
  { type: "Walk", date: localDay(17), distance_m: 2800, duration_min: 35 },
];

const DEMO_DAILY_ACTIVITY = [
  { date: localDay(0), steps: 8241,  distance_m: 6200 },
  { date: localDay(1), steps: 11382, distance_m: 8900 },
  { date: localDay(2), steps: 5021,  distance_m: 3800 },
  { date: localDay(3), steps: 9875,  distance_m: 7400 },
  { date: localDay(4), steps: 7430,  distance_m: 5600 },
  { date: localDay(5), steps: 12100, distance_m: 9200 },
  { date: localDay(6), steps: 6543,  distance_m: 4900 },
];

const demoSleepAt = (daysAgo, startHour, startMin, mins) => {
  const end = new Date(Date.now() - daysAgo * 86400000);
  end.setHours(7, 10, 0, 0);
  const start = new Date(end.getTime());
  start.setDate(start.getDate() - 1);
  start.setHours(startHour, startMin, 0, 0);
  return {
    date: localDay(daysAgo), asleep_min: mins, in_bed_min: mins + 35,
    sleep_start_at: start.toISOString(), sleep_end_at: end.toISOString(),
  };
};
const DEMO_SLEEP = [
  demoSleepAt(0, 23, 10, 432),
  demoSleepAt(1, 23, 40, 405),
  demoSleepAt(2, 22, 55, 465),
  demoSleepAt(3, 23, 20, 421),
  demoSleepAt(4, 0, 15, 372),
  demoSleepAt(5, 23, 5, 448),
  demoSleepAt(6, 23, 30, 412),
];

function fmtSleepMin(min) {
  const h = Math.floor(min / 60), m = Math.round(min % 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

function fmtClock(iso) {
  return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

// Median bedtime over measured nights, wraparound-safe: minutes past 18:00
// (mod 24h) makes 11pm..2am a monotonic range instead of splitting at midnight.
function medianBedtime(nights) {
  if (nights.length === 0) return null;
  const keyed = nights.map(n => {
    const d = new Date(n.sleep_start_at);
    return { n, k: (d.getHours() * 60 + d.getMinutes() + 1440 - 18 * 60) % 1440 };
  }).sort((a, b) => a.k - b.k);
  return keyed[Math.floor(keyed.length / 2)].n.sleep_start_at;
}

function buildWeeklyChart(workouts) {
  const data = [];
  for (let i = 3; i >= 0; i--) {
    const weekStart = localDay((i + 1) * 7);
    const weekEnd   = localDay(i * 7);
    const week = workouts.filter(w => w.date >= weekStart && w.date < weekEnd);
    const miles = week.reduce((s, w) => s + (w.distance_m || 0) / 1609.34, 0);
    data.push({ week: `W${4 - i}`, count: week.length, miles: parseFloat(miles.toFixed(1)) });
  }
  return data;
}

function build7DayStepsChart(dailyActivity) {
  const data = [];
  for (let i = 6; i >= 0; i--) {
    const dateStr = localDay(i);
    const row = dailyActivity.find(r => r.date === dateStr);
    const d = new Date(Date.now() - i * 86400000);
    const dayLabel = d.toLocaleDateString('en-US', { weekday: 'short' }).slice(0, 3);
    data.push({ day: dayLabel, steps: row?.steps || 0, dateStr, distance_m: row?.distance_m || 0 });
  }
  return data;
}

export function AppleHealthSection({ user, isDemo }) {
  const [workouts, setWorkouts]               = useState([]);
  const [dailyActivity, setDailyActivity]     = useState([]);
  const [sleepNights, setSleepNights]         = useState([]);
  const [loading, setLoading]                 = useState(false);
  const [synced, setSynced]                   = useState(null); // ISO string of last sync or null
  const [hkLifetimeCount, setHkLifetimeCount] = useState(null);
  const [selectedBar, setSelectedBar]         = useState(null);
  const authStatus = useHealthAuthStatus();

  const loadData = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    const since28 = localDay(28);
    // Trailing 7 days INCLUSIVE of today = today + the previous 6. localDay(7)
    // was an 8-day window, so "Miles this week" disagreed with the weekly-goal
    // resolvers (getGoalAutoValue / notify-cron), which use daysAgoStr(6) —
    // the card said 29.1 while the goal saw 27.4. One window, everywhere.
    const since7  = localDay(6);
    const [{ data: wData }, { count: hkCount }, { data: profile }, { data: actData }, { data: sleepData }] = await Promise.all([
      supabase
        .from("workouts")
        .select("type, date, distance_m, duration_min, source")
        .eq("user_id", user.id)
        .gte("date", since28)
        .order("date", { ascending: false }),
      supabase
        .from("workouts")
        .select("id", { count: 'exact', head: true })
        .eq("user_id", user.id)
        .eq("source", "healthkit"),
      supabase
        .from("profiles")
        .select("health_last_sync_at")
        .eq("id", user.id)
        .single(),
      supabase
        .from("daily_activity")
        .select("date, steps, distance_m")
        .eq("user_id", user.id)
        .gte("date", since7)
        .order("date", { ascending: false }),
      supabase
        .from("sleep_nights")
        .select("date, asleep_min, in_bed_min, sleep_start_at, sleep_end_at")
        .eq("user_id", user.id)
        .order("date", { ascending: false })
        .limit(14),
    ]);
    setWorkouts(wData || []);
    setHkLifetimeCount(hkCount ?? 0);
    setSynced(profile?.health_last_sync_at || null);
    setDailyActivity(actData || []);
    setSleepNights(sleepData || []);
    setLoading(false);
  }, [user?.id]);

  useEffect(() => {
    if (isDemo) { setWorkouts(DEMO_WORKOUTS); setDailyActivity(DEMO_DAILY_ACTIVITY); setSleepNights(DEMO_SLEEP); setSynced("demo"); return; }
    loadData();
    // Refresh when a HealthKit sync lands (healthSync.js dispatches this) —
    // same signal the dashboard listens for, so the card and the goal cards
    // can never show different vintages of the same data.
    const onSynced = () => loadData();
    window.addEventListener("scorify-health-synced", onSynced);
    return () => window.removeEventListener("scorify-health-synced", onSynced);
  }, [user?.id, isDemo, loadData]);

  const todayStr      = localDay(0);
  const weekAgoStr    = localDay(7);
  const stepsData     = build7DayStepsChart(dailyActivity);
  const weeklyData    = buildWeeklyChart(workouts);

  // ── Steps today (fall back to most recent day) ────────────────────────────
  const todayRow      = dailyActivity.find(r => r.date === todayStr);
  const latestRow     = !todayRow && dailyActivity.length > 0 ? dailyActivity[0] : null;
  const stepsDisplay  = todayRow?.steps ?? latestRow?.steps ?? null;
  const stepsLabel    = todayRow
    ? "Steps today"
    : latestRow
      ? `Steps ${new Date(latestRow.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
      : "Steps today";

  // ── Miles this week (from daily_activity, not workouts) ───────────────────
  const milesThisWeek = dailyActivity.reduce((s, r) => s + (r.distance_m || 0), 0) / 1609.34;

  // ── Active days (days in last 7 with steps > 0) ──────────────────────────
  const activeDays = dailyActivity.filter(r => (r.steps || 0) > 0).length;

  // ── Workouts this week (healthkit only) ───────────────────────────────────
  const hkThisWeek = workouts.filter(w => w.date >= weekAgoStr && w.source === 'healthkit');
  const hasWorkouts = hkThisWeek.length > 0;
  const hasActivity = dailyActivity.length > 0;

  // ── Daily average (mean steps last 7 days, rounded to nearest 100) ──────
  const dailyAvg = hasActivity
    ? Math.round(dailyActivity.reduce((s, r) => s + (r.steps || 0), 0) / dailyActivity.length / 100) * 100
    : null;

  // ── Sleep — THE inBed HONESTY RULE ───────────────────────────────────────
  // A night is MEASURED only when asleep_min is non-null (asleep-family
  // HealthKit samples: Watch worn overnight or a sleep app). inBed-only rows
  // come from an iPhone Sleep Schedule and mirror the configured schedule,
  // not actual sleep — they must NEVER render as a duration, because a number
  // implies measurement. No rows at all renders as unknown, never as 0h.
  const measuredNights  = sleepNights.filter(n => n.asleep_min != null);
  const inBedOnlyNights = sleepNights.filter(n => n.asleep_min == null && n.in_bed_min != null);
  const lastMeasured    = measuredNights[0] || null;
  const recentMeasured  = measuredNights.slice(0, 7);
  const avgSleepMin     = recentMeasured.length > 0
    ? recentMeasured.reduce((s, n) => s + n.asleep_min, 0) / recentMeasured.length
    : null;
  const bedtimeIso      = medianBedtime(measuredNights);
  const lastNightLabel  = lastMeasured
    ? (lastMeasured.date === todayStr
        ? "Sleep last night"
        : `Sleep, night of ${new Date(lastMeasured.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`)
    : null;

  // Denial guard: synced but HealthKit NEVER returned anything — access was
  // denied at the first prompt.
  const isDenied = !isDemo && !!synced && hkLifetimeCount === 0 && !hasActivity;

  // Revocation guard. isDenied above cannot catch this: it requires
  // hkLifetimeCount === 0, so a user who synced successfully and LATER turned
  // Scorify off in Settings > Privacy & Security > Health keeps a non-zero
  // lifetime count and falls through to the normal card — showing a "Connected"
  // badge and a recent "Last synced" date over empty stats.
  //
  // HealthKit returns empty results rather than an error when access is
  // withdrawn, and healthSync writes health_last_sync_at on every run including
  // zero-result ones, so neither the timestamp nor an error can reveal this. An
  // empty 7-day activity window is the only available signal: daily_activity
  // skips only days where steps AND distance are both zero, which does not
  // happen for someone carrying an iPhone, so no rows in 7 days means the data
  // is not arriving rather than that the user sat still.
  const isStalled = !isDemo && !!synced && hkLifetimeCount > 0 && !hasActivity;

  const lastSyncText = synced && synced !== "demo"
    ? new Date(synced).toLocaleDateString("en-US", { month: "short", day: "numeric" })
    : null;

  // Badge. "Connected" is claimed only when data is actually arriving — saying
  // it over an empty card is the misreport this guard exists to prevent.
  const badge = (isDenied || isStalled)
    ? { label: "Not receiving data", color: D.accent, bg: withAlpha(T.accentFill,0.08), border: withAlpha(T.accentFill,0.25) }
    : synced
    ? { label: "Connected", color: D.success, bg: withAlpha(T.success,0.1), border: withAlpha(T.success,0.3) }
    : { label: "Syncing",   color: D.accent,  bg: withAlpha(T.accentFill,0.08), border: withAlpha(T.accentFill,0.25) };

  // Authorization failed this launch (sheet never opened, retry also failed):
  // never show "Connected" or a skeleton over it — show the fallback instead.
  if (!isDemo && authStatus === "failed") {
    return (
      <div style={D.card}>
        <div style={{ ...D.title, marginBottom: 14 }}>Apple Health</div>
        <HealthAuthNotice userId={user?.id} />
      </div>
    );
  }

  if (loading) {
    return (
      <div style={D.card}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 10 }}>
          {[1, 2, 3, 4].map(i => (
            <div key={i} style={{ ...D.inner, height: 60, opacity: 0.5 }} />
          ))}
        </div>
      </div>
    );
  }

  if (isDenied || isStalled) {
    return (
      <div style={D.card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <div style={D.title}>Apple Health</div>
          <div style={{ fontSize: 11, fontWeight: 600, color: badge.color, background: badge.bg, border: `1px solid ${badge.border}`, borderRadius: 20, padding: "3px 10px" }}>{badge.label}</div>
        </div>
        <div style={{ ...D.inner, fontSize: 13, color: D.text, lineHeight: 1.6 }}>
          {isStalled
            ? <>Your activity data has stopped reaching Scorify. This usually means Health access was turned off. Check Scorify under Settings &gt; Privacy &amp; Security &gt; Health.</>
            : <>No data found. Check Scorify under Settings &gt; Health &gt; Data Access &amp; Devices.</>}
        </div>
      </div>
    );
  }

  return (
    <div style={D.card}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
        <div>
          <div style={D.title}>Apple Health</div>
          {lastSyncText && <div style={D.sub}>Last synced {lastSyncText}</div>}
          {!lastSyncText && !isDemo && <div style={D.sub}>Open the app on your iPhone to sync.</div>}
        </div>
        <div style={{ fontSize: 11, fontWeight: 600, color: badge.color, background: badge.bg, border: `1px solid ${badge.border}`, borderRadius: 20, padding: "3px 10px" }}>
          {badge.label}
        </div>
      </div>

      {/* Stat cards — 2x2 grid */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 10, marginBottom: (hasWorkouts || hasActivity) ? 18 : 0 }}>
        {[
          { label: stepsLabel,          value: stepsDisplay != null ? stepsDisplay.toLocaleString() : "—" },
          { label: "Miles this week",   value: milesThisWeek > 0 ? `${milesThisWeek.toFixed(1)} mi` : "—" },
          { label: "Active days",       value: activeDays > 0 ? `${activeDays} / 7` : "—" },
          { label: "Daily average",      value: dailyAvg != null ? dailyAvg.toLocaleString() : "—" },
        ].map(s => (
          <div key={s.label} style={D.inner}>
            <div style={{ fontSize: 18, fontWeight: 800, color: D.text, marginBottom: 2 }}>{s.value}</div>
            <div style={{ fontSize: 11, color: D.muted }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* ── Sleep ── measured nights only; a schedule artifact never becomes a number */}
      {measuredNights.length > 0 ? (
        <div style={{ marginBottom: 18 }}>
          <div style={{ fontSize: 12, color: D.muted, marginBottom: 10 }}>Sleep (from Apple Health)</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10 }}>
            {[
              { label: lastNightLabel, value: fmtSleepMin(lastMeasured.asleep_min) },
              { label: `Avg, last ${recentMeasured.length === 1 ? "night" : `${recentMeasured.length} nights`}`, value: avgSleepMin != null ? fmtSleepMin(avgSleepMin) : "—" },
              { label: "Typical bedtime", value: bedtimeIso ? fmtClock(bedtimeIso) : "—" },
            ].map(s => (
              <div key={s.label} style={D.inner}>
                <div style={{ fontSize: 16, fontWeight: 800, color: D.text, marginBottom: 2 }}>{s.value}</div>
                <div style={{ fontSize: 11, color: D.muted }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      ) : inBedOnlyNights.length > 0 ? (
        <div style={{ ...D.inner, marginBottom: 18, fontSize: 12, color: D.text, lineHeight: 1.6 }}>
          <div style={{ fontWeight: 700, marginBottom: 3 }}>Sleep: schedule detected, no measured sleep</div>
          {/* State A: inBed rows exist → sleep access IS granted. Say so definitively and NEVER
              suggest checking permission here — that would contradict the proof. No chip either. */}
          Your iPhone is sharing a sleep schedule with Health, so access is on, but a schedule
          follows your alarm settings, not your actual sleep, so Scorify doesn&rsquo;t count it. An Apple
          Watch worn overnight records real sleep, as do Oura, Whoop, Garmin and Fitbit once you turn
          on Health sharing in their own app, and sleep apps like AutoSleep, Sleep Cycle or Pillow.
        </div>
      ) : (
        <div style={{ marginBottom: 18 }}>
          {/* State B: zero rows — AMBIGUOUS. No permission and no data are indistinguishable here
              (HealthKit returns empty for read denial by design), so assert NO cause; name both
              paths. The chip below scopes honesty to sleep: the card badge is card-wide and
              activity-based, so it can read "Connected" while this section is empty — the chip stops
              the green badge from implying sleep, without touching the (correct) activity claim.
              Muted, not alarming: this is a state, not an error. State B only. */}
          <span style={{ display: "inline-block", fontSize: 11, fontWeight: 600, color: D.muted, background:T.overlayLight(0.04), border: `1px solid ${T.overlayLight(0.08)}`, borderRadius: 20, padding: "3px 10px", marginBottom: 8 }}>Not receiving sleep</span>
          <div style={{ fontSize: 12, color: D.muted, lineHeight: 1.5 }}>
            <span style={{ fontWeight: 700, color: D.text }}>No sleep data showing.</span> This can mean one of two things,
            and Scorify can&rsquo;t tell which: sleep isn&rsquo;t being shared with it, or nothing is recording sleep yet.
            <div style={{ marginTop: 6 }}>&bull; <span style={{ fontWeight: 700 }}>Check sharing:</span> turn Sleep on for Scorify in Settings &rarr; Health &rarr; Data Access &amp; Devices.</div>
            <div style={{ marginTop: 4 }}>&bull; <span style={{ fontWeight: 700 }}>Check something&rsquo;s recording it:</span> an Apple Watch worn overnight logs sleep automatically. Oura, Whoop, Garmin and Fitbit can too, once you enable Health sharing in their app, as can AutoSleep, Sleep Cycle or Pillow.</div>
          </div>
        </div>
      )}

      {/* 7-day steps bar chart — tappable */}
      {hasActivity && (
        <div style={{ marginBottom: hasWorkouts ? 18 : 4 }}>
          <div style={{ fontSize: 12, color: D.muted, marginBottom: 10 }}>Steps (last 7 days)</div>
          <ResponsiveContainer width="100%" height={110}>
            <BarChart data={stepsData} barSize={24} margin={{ top: 4, right: 4, bottom: 0, left: -28 }}>
              <XAxis dataKey="day" tick={{ fill: D.muted, fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: D.muted, fontSize: 10 }} axisLine={false} tickLine={false} allowDecimals={false}
                tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
              <Bar dataKey="steps" radius={[4, 4, 0, 0]}
                onClick={(_data, index) => setSelectedBar(prev => prev === index ? null : index)}
                style={{ cursor: "pointer" }}>
                {stepsData.map((_, i) => (
                  <Cell key={i} fill={i === selectedBar ? D.accent : withAlpha(T.accentFill,0.2)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          {selectedBar != null && stepsData[selectedBar] && (
            <div style={{ ...D.inner, marginTop: 10 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: D.text }}>
                {new Date(stepsData[selectedBar].dateStr + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
              </div>
              <div style={{ fontSize: 12, color: D.muted, marginTop: 4 }}>
                {stepsData[selectedBar].steps.toLocaleString()} steps{stepsData[selectedBar].distance_m > 0 ? ` · ${(stepsData[selectedBar].distance_m / 1609.34).toFixed(1)} mi` : ''}
              </div>
            </div>
          )}
        </div>
      )}

      {/* 4-week workout bar chart — only when user has healthkit workouts */}
      {hasWorkouts && (
        <div style={{ marginBottom: 4 }}>
          <div style={{ fontSize: 12, color: D.muted, marginBottom: 10 }}>Workouts per week (last 4 weeks)</div>
          <ResponsiveContainer width="100%" height={110}>
            <BarChart data={weeklyData} barSize={28} margin={{ top: 4, right: 4, bottom: 0, left: -28 }}>
              <XAxis dataKey="week" tick={{ fill: D.muted, fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: D.muted, fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
              <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                {weeklyData.map((_, i) => (
                  <Cell key={i} fill={i === weeklyData.length - 1 ? D.accent : withAlpha(T.accentFill,0.2)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
